import React from "react";
import {
  Box,
  Button,
  Grid2 as Grid,
  Grid2Props,
  IconButton,
  Typography,
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import FormLabel from "../base/FormLabel";
import { singleGridItem } from "../../fields/defaultStyles";
import ArraySubFormProvider from "../../fields/subForms/array/ArraySubFormProvider";
import ArraySubFormList from "../../fields/subForms/array/ArraySubFormList";
import ArraySubFormAdd from "../../fields/subForms/array/ArraySubFormAdd";
import { useFormHandlerContext } from "../../../../controllers/formController";
import FormAutoCompleteField from "../../fields/basic/AutoCompleteField";
import GroupGridContainer from "../../../common/GroupGridContainer";

export type LanguagesList = LanguageItem[];

export interface LanguageItem {
  languages: string[];
  roles: string[];
}

const formatLanguagesItem = (input: LanguageItem) => {
  const { languages, roles } = input;
  return (
    <>
      {languages && (
        <>
          <Typography
            component="span"
            sx={{ whiteSpace: "nowrap", mr: 0.5 }}
            fontWeight="bold"
          >
            Languages:
          </Typography>
          <Typography component="span" sx={{ whiteSpace: "nowrap", mr: 0.5 }}>
            {languages.filter(Boolean).join(", ") || "None"}
          </Typography>
        </>
      )}
      {roles && (
        <>
          <Typography
            component="span"
            sx={{ whiteSpace: "nowrap", ml: 2, mr: 0.5 }}
            fontWeight="bold"
          >
            Roles:
          </Typography>
          <Typography component="span" sx={{ whiteSpace: "nowrap", mr: 0.5 }}>
            {roles.filter(Boolean).join(", ") || "None"}
          </Typography>
        </>
      )}
    </>
  );
};

export const formatLanguagesList = (input: LanguagesList) => {
  if (!input || input.length === 0) return "No Languages";
  return (
    <Box>
      {input.map((item, index) => (
        <Box key={index} sx={{ mb: 1, display: "flex", flexWrap: "nowrap" }}>
          <Typography
            component="span"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap", mr: 0.5 }}
          >
            {`${index + 1}. `}
          </Typography>
          <Box
            sx={{ display: "flex", flexWrap: "nowrap", alignItems: "center" }}
          >
            {formatLanguagesItem(item)}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

interface LanguagesListFieldProps extends Grid2Props {
  name: string;
  label: string;
  rowLabel: string;
  disabled?: boolean;
  rolesPlaceHolder?: string | ((index: number) => string);
  languagesPlaceHolder?: string | ((index: number) => string);
  rolesLabel: string | ((index: number) => string);
  languagesLabel: string | ((index: number) => string);
  rolesEnum: string;
  languagesEnum: string;
}

const LanguagesListField: React.FC<LanguagesListFieldProps> = ({
  name,
  rowLabel,
  label,
  rolesLabel,
  rolesPlaceHolder,
  languagesPlaceHolder,
  languagesEnum,
  languagesLabel,
  rolesEnum,
  disabled = false,
  ...grid
}) => {
  const { isProcessing } = useFormHandlerContext();
  return (
    <Grid container spacing={1} {...singleGridItem} {...grid}>
      <FormLabel label={label} name={name} />
      <ArraySubFormProvider name={name}>
        <ArraySubFormList
          renderItem={(index, remove) => (
            <GroupGridContainer key={index}>
              <Grid {...singleGridItem}>
                <FormLabel
                  sx={{ fontSize: "1.2rem" }}
                  label={`${rowLabel} ${index + 1}`}
                  actions={
                    <IconButton
                      color="error"
                      onClick={() => remove()}
                      size="small"
                      disabled={disabled || isProcessing}
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                  }
                />
              </Grid>
              <FormAutoCompleteField
                name="languages"
                label={
                  typeof languagesLabel === "function"
                    ? languagesLabel(index + 1)
                    : languagesLabel
                }
                placeholder={
                  typeof languagesPlaceHolder === "function"
                    ? languagesPlaceHolder(index + 1)
                    : languagesPlaceHolder
                }
                useEnum={languagesEnum}
                disabled={disabled || isProcessing}
                multiple
                required
              />
              <FormAutoCompleteField
                name="roles"
                label={
                  typeof rolesLabel === "function"
                    ? rolesLabel(index + 1)
                    : rolesLabel
                }
                placeholder={
                  typeof rolesPlaceHolder === "function"
                    ? rolesPlaceHolder(index + 1)
                    : rolesPlaceHolder
                }
                useEnum={rolesEnum}
                disabled={disabled || isProcessing}
                multiple
                required
              />
            </GroupGridContainer>
          )}
        />
        <Grid {...singleGridItem}>
          <ArraySubFormAdd
            render={(append) => (
              <Button
                variant="contained"
                fullWidth
                color="primary"
                startIcon={<AddCircleOutline />}
                disabled={disabled || isProcessing}
                onClick={() =>
                  append({
                    languages: [],
                    roles: [],
                  })
                }
              >
                Add {rowLabel}
              </Button>
            )}
          />
        </Grid>
      </ArraySubFormProvider>
    </Grid>
  );
};

export default LanguagesListField;
